import { getHashedString } from '@/common/api/utils/utils';
import { getSafeSelectedTheme } from '@/common/utils/chartUtils';
import {
  DEFAULT_WIDGET_COLOR_OPTION,
  DEFAULT_WIDGET_TITLE_STYLE,
  DISPLAY_STYLE_KEY,
  WIDGET_CHART_TYPE_KEY,
  FIXED_DATA_ID_BY_CHART_TYPE,
} from '@/dashboard/features/widgets/common/constants/define';
import { getDefaultWidgetSize } from '@/dashboard/features/dashboardLayout/domain/baseGridLayout.constants';
import type { ClusterValueTags } from '@/dashboard/features/widgets/cluster/settings/domain/clusterData';
import { cloneDeep, isArray, isNil, omit } from 'lodash-es';
import {
  DashboardWidgetGroupValueV8,
  WidgetV8MappingResponseItem,
} from '@/api-client/metav8Over/model';
import { UnitOverride } from '@/dashboard/features/chartDataHandler/domain/unit';
import { BucketAggregationFactory } from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/elasticsearch/bucketSettings/BucketAggregationFactory';
import { MetricsAggregationFactory } from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/elasticsearch/metricsSettings/MetricsAggregationFactory';
import { useWidgetUUIDStore } from '@/dashboard/stores/widget-uuid';
import { applyDefaultChartOptions } from '@/dashboard/features/widgets/common/domain/chartOption';
import { getWidgetCompName } from '@/dashboard/features/widgets/common/domain/widgetName';
import { isByTargetChartType } from '@/dashboard/features/widgets/common/domain/widgetTypeGuard';
import {
  GridLayoutItem,
  WidgetChartData,
  WidgetChartType,
} from '@/dashboard/shared/types/widgets.types';
import {
  chartTypeMap,
  createTypeDtoMap,
  createTypeMap,
  dataResourceMap,
  DEFAULT_DATA_RESOURCE,
  displayStyleMap,
  labelFormatTypeMap,
  targetTagTypeMap,
  timePeriodMap,
  widgetDataTypeMap,
  widgetSeriesTypeMap,
  widgetTypeMap,
} from '@/dashboard/features/dashboardCore/constants/widgets.data';
import {
  WidgetInPort,
  WidgetOutPort,
} from '@/dashboard/features/dashboardCore/services/widgets.ports';
import {
  syncTargetsToValueTags,
  ClusterWidgetChartData,
} from '@/dashboard/features/widgets/cluster/settings/domain/clusterData';
import { Selectbox2FilterData } from '@/dashboard/features/widgets/filter/widget/domain/filterData.types';
import { ReferenceInfo } from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/common/referenceSettingOption/referenceSettingOption.setup';
import { forEachGridLayoutItems } from '@/dashboard/features/dashboardLayout/domain/gridLayoutItem';
import {
  ConnectLinkOption,
  LinkOption,
} from '@/dashboard/features/widgetSettings/editModeWidgetSettings/components/common/linkSettingOption.types';
import { isValidJson } from '@/common/utils/validation';

// 레거시 데이터 구조를 위한 타입 정의
interface LegacyChartOption {
  linkOptions?: Exclude<ConnectLinkOption, 'category' | 'openMethod'>[];
}

interface NewChartOption {
  linkOption?: LinkOption;
  unitOverride?: UnitOverride[];
  referenceInfo?: ReferenceInfo;
}

type SafeChartOption = Record<string, unknown> & LegacyChartOption & NewChartOption;

const convertTitleOption: WidgetInPort['convertTitleOption'] = (titleOptionDto) => {
  return titleOptionDto && isValidJson(titleOptionDto)
    ? JSON.parse(titleOptionDto)
    : {
        showTitle: true,
        titleText: '',
        titleStyle: cloneDeep(DEFAULT_WIDGET_TITLE_STYLE),
      };
};

const convertToWidgetColor: WidgetInPort['convertToWidgetColor'] = (widgetColorDto) => {
  return widgetColorDto && isValidJson(widgetColorDto)
    ? JSON.parse(widgetColorDto)
    : cloneDeep(DEFAULT_WIDGET_COLOR_OPTION);
};

const convertToChartOption: WidgetInPort['convertToChartOption'] = (chartOptionDto) => {
  if (chartOptionDto && isValidJson(chartOptionDto)) {
    const parsed = JSON.parse(chartOptionDto);
    return {
      ...parsed,
      referenceInfo: parsed.referenceInfo
        ? Object.entries(parsed.referenceInfo).reduce((acc, [chartDataIndex, variables]) => {
            acc[chartDataIndex] = (variables as any).map(({ key, value }) => {
              let convertedValue = value;
              if (value?.type === 'customVariable') {
                const isSingleVariableSetted = value.widgetId && value.filterId;
                const isMultiVariableSetted = value.selected && value.selected.length > 0;

                if (isSingleVariableSetted && !isMultiVariableSetted) {
                  convertedValue = {
                    type: 'customVariable',
                    selected: [
                      {
                        widgetId: value.widgetId,
                        filterId: value.filterId,
                        timeType: value.timeType,
                      },
                    ],
                  };
                }
              }

              return {
                key,
                value: convertedValue,
              };
            });
            return acc;
          }, {})
        : {},
    };
  }
  return {};
};

const convertToCreateType: WidgetInPort['convertToCreateType'] = (createTypeDto) => {
  if (isNil(createTypeDto)) {
    return 'base';
  }
  const createType = createTypeMap[createTypeDto];
  if (!createType) {
    throw new Error(`${createTypeDto} createType이 추가되었습니다.`);
  }

  return createType;
};

const convertToFilterOption: WidgetInPort['convertToFilterOption'] = (filterOptionDto) => {
  return filterOptionDto && isValidJson(filterOptionDto) ? JSON.parse(filterOptionDto) : undefined;
};

export const extractFilterData = (
  filterList: NonNullable<GridLayoutItem['filterOption']>['filterList'],
): GridLayoutItem['filterData'] => {
  return filterList
    .filter(({ filter }) => filter.filterType === 'selectbox2' && !isNil(filter.stat))
    .map(({ filter }) => ({
      dataId: (filter as Selectbox2FilterData).stat!.statId,
      category: (filter as Selectbox2FilterData).stat!.childCategory,
    }));
};

const convertToCalendarTimeRange: WidgetInPort['convertToCalendarTimeRange'] = (
  calendarTimeRangeDto,
) => {
  return calendarTimeRangeDto && isValidJson(calendarTimeRangeDto)
    ? JSON.parse(calendarTimeRangeDto)
    : undefined;
};

const convertToDataResource: WidgetInPort['convertToDataResource'] = (dataResourceDto = '') => {
  return dataResourceMap[dataResourceDto] ?? DEFAULT_DATA_RESOURCE;
};

const convertToWidgetType: WidgetInPort['convertToWidgetType'] = (widgetTypeDto) => {
  if (!widgetTypeDto) {
    return 'GRAPHS';
  }
  const widgetType = widgetTypeMap[widgetTypeDto];
  if (!widgetType) {
    throw new Error(`${widgetTypeDto} widgetType이 추가되었습니다.`);
  }

  return widgetType;
};

const convertToChartType: WidgetInPort['convertToChartType'] = (chartTypeDto) => {
  if (!chartTypeDto) {
    return 'TIME_SERIES';
  }
  const chartType = chartTypeMap[chartTypeDto];
  if (!chartType) {
    throw new Error(`${chartTypeDto} chartType이 추가되었습니다.`);
  }

  return chartType;
};

const convertToDisplayStyle: WidgetInPort['convertToDisplayStyle'] = (displayStyleDto) => {
  if (isNil(displayStyleDto) || displayStyleDto === '') {
    return '';
  }
  const displayStyle = displayStyleMap[displayStyleDto];
  if (!displayStyle) {
    throw new Error(`${displayStyleDto} displayStyle이 추가되었습니다.`);
  }

  return displayStyle;
};

const convertToTimePeriod: WidgetInPort['convertToTimePeriod'] = (timePeriodDto) => {
  if (isNil(timePeriodDto)) {
    return undefined;
  }
  const timePeriod = timePeriodMap[timePeriodDto];
  if (!timePeriod) {
    throw new Error(`${timePeriodDto} timePeriod이 추가되었습니다.`);
  }

  return timePeriod;
};

const convertToWidgetSeriesType: WidgetInPort['convertToWidgetSeriesType'] = (
  seriesTypeDto,
  chartType,
) => {
  if (isNil(seriesTypeDto)) {
    return undefined;
  }
  if (chartType && isByTargetChartType(chartType)) {
    return 'byTarget';
  }
  const seriesType = widgetSeriesTypeMap[seriesTypeDto];
  if (!seriesType) {
    throw new Error(`${seriesTypeDto} seriesType이 추가되었습니다.`);
  }

  return seriesType;
};

const convertToWidgetDataType: WidgetInPort['convertToWidgetDataType'] = (dataTypeDto) => {
  if (isNil(dataTypeDto)) {
    return undefined;
  }
  const dataType = widgetDataTypeMap[dataTypeDto];
  if (!dataType) {
    throw new Error(`${dataTypeDto} dataType이 추가되었습니다.`);
  }

  return dataType;
};

const convertToTargetTagType: WidgetInPort['convertToTargetTagType'] = (tagTypeDto) => {
  const tagType = targetTagTypeMap[tagTypeDto];
  if (!tagType) {
    throw new Error(`${tagTypeDto} tagType이 추가되었습니다.`);
  }

  return tagType;
};

const covertToChartData: WidgetInPort['covertToChartData'] = (
  chartDataDto,
  widgetId: string,
  idx: number,
  chartType?: WidgetChartType,
  options?: { unitOverride?: UnitOverride },
) => {
  const parsedExtraResourceOption = JSON.parse(chartDataDto.extraResourceOption ?? '{}');
  const { dataId, targets, value } = chartDataDto;

  return {
    ...omit(chartDataDto, 'extraResourceOption'),
    id:
      (chartDataDto as any)?.id ||
      getHashedString({
        dataId,
        targets,
        widgetId,
        value,
        idx,
      }),
    category: chartDataDto.category ?? '',
    seriesType:
      chartDataDto.dataId === FIXED_DATA_ID_BY_CHART_TYPE.TOPOLOGY
        ? 'byTarget'
        : convertToWidgetSeriesType(chartDataDto.seriesType, chartType),
    dataType: convertToWidgetDataType(chartDataDto.dataType),
    targets: chartDataDto.targets.map((target) => ({
      ...target,
      tagType: convertToTargetTagType(target.tagType),
      tagValue: target.tagValueId ?? '',
    })),
    value:
      chartDataDto.value && isValidJson(chartDataDto.value) ? JSON.parse(chartDataDto.value) : '',
    externalOptions: {
      ...omit(
        parsedExtraResourceOption,
        'labelFormatType',
        'labelFormat',
        'query',
        'buckets',
        'metrics',
      ),
      labelFormatType: labelFormatTypeMap[parsedExtraResourceOption.labelFormatType ?? 'labelKey'],
      labelFormat: parsedExtraResourceOption.labelFormat ?? '',
      query: parsedExtraResourceOption.query ?? '',
      buckets: parsedExtraResourceOption.buckets?.map((bucket) => {
        const bucketAggregation = BucketAggregationFactory.createBucketAggregation(
          bucket.type,
          bucket.show,
        );
        bucketAggregation.configure(bucket.config);
        return bucketAggregation;
      }),
      metrics: parsedExtraResourceOption.metrics?.map((metric) => {
        const metricAggregation = MetricsAggregationFactory.createMetricsAggregation(
          metric.type,
          metric.show,
        );
        metricAggregation.configure(metric.config);
        return metricAggregation;
      }),
    },
    unitOverride: options?.unitOverride,
  };
};

const convertToExtraData: WidgetInPort['convertToExtraData'] = (extraDataDto) => {
  return extraDataDto?.map((extraData) => ({
    extraId: extraData.extraId ?? '',
    targets:
      extraData.targets?.map((target) =>
        target.map((item) => ({
          id: item.id ?? '',
          value: item.value ?? '',
        })),
      ) ?? [],
  }));
};

const processReferenceInfo = (
  chartDataList: WidgetChartData[],
  chartOption: any,
): ReferenceInfo | undefined => {
  if (!chartOption?.referenceInfo) {
    return undefined;
  }
  const newReferenceInfo: ReferenceInfo = { ...chartOption.referenceInfo };
  chartDataList.forEach((chartData, index) => {
    const referenceData = chartOption?.referenceInfo?.[`${index}`];
    newReferenceInfo[`${index}`] = referenceData?.map((reference) => ({
      ...reference,
      key: { ...reference.key, statId: chartData.dataId },
    }));
  });
  return newReferenceInfo;
};

/**
 * 기존 ALERT 위젯의 여러 chartData(각각 단일 category)를
 * 하나의 chartData(categories 배열)로 합치는 마이그레이션
 *
 * 마이그레이션 대상:
 * 1. 레거시: 여러 chartData, 각각 단일 category → 하나로 합침
 * 2. value.categories: 이전 저장 방식(value 안에 categories) → 최상위로 이동
 */
const migrateAlertChartData = (
  chartDataList: WidgetChartData[] | undefined,
): WidgetChartData[] | undefined => {
  if (!chartDataList || chartDataList.length === 0) {
    return chartDataList;
  }

  const [first] = chartDataList;

  // 이미 최상위 categories에 유효한 값이 있으면 마이그레이션 완료
  if (first.categories?.length) {
    return chartDataList;
  }

  // value.categories에서 최상위로 이동 (이전 저장 방식 호환)
  if (first.value?.categories?.length > 0) {
    first.categories = first.value.categories;
    delete first.value.categories;
    return chartDataList;
  }

  // 레거시: 여러 chartData의 단일 category를 합침
  const categories = chartDataList.map((cd) => cd.category).filter(Boolean);

  if (categories.length === 0) {
    return chartDataList;
  }

  if (!first.value || typeof first.value !== 'object') {
    first.value = {};
  }

  first.categories = categories;
  first.targets = chartDataList.flatMap((cd) => cd.targets);
  first.category = '';

  return [first];
};

export const convertToGridLayoutItem: WidgetInPort['convertToGridLayoutItem'] = (
  widgetChildValue,
) => {
  const chartType = convertToChartType(widgetChildValue.chartType);
  // NOTE: 기존 scatter 위젯의 빈 displayStyle('')을 SCATTER_BASIC으로 마이그레이션
  const convertedDisplayStyle = convertToDisplayStyle(widgetChildValue.displayStyle);
  const displayStyle =
    chartType === 'SCATTER' && convertedDisplayStyle === ''
      ? DISPLAY_STYLE_KEY.SCATTER_BASIC
      : convertedDisplayStyle;

  const titleOption = convertTitleOption(widgetChildValue.titleOption);

  const widgetSize = getDefaultWidgetSize(chartType, displayStyle);

  const { generateWidgetUUID } = useWidgetUUIDStore();

  const i = widgetChildValue.widgetUUID ?? generateWidgetUUID();
  const filterOption = convertToFilterOption(widgetChildValue.filterOption);

  // @ts-expect-error TODO: FIXME
  const chartData = widgetChildValue.chartData?.map(covertToChartData);

  const chartOption = applyDefaultChartOptions(
    chartType,
    convertToChartOption(widgetChildValue.chartOption),
  );

  const createType = convertToCreateType(widgetChildValue.createType);

  const referenceInfo =
    chartData && createType === 'reference'
      ? processReferenceInfo(chartData, chartOption)
      : undefined;

  // NOTE: 07 -> 08 linkOption관련 데이터 구조 변경으로, 이전 버전의 데이터가 있다면 변경
  const safeChartOption = chartOption as SafeChartOption;
  const isNewLinkOptionExist = safeChartOption?.linkOption;
  const isOldLinkOptionsExist = isArray(safeChartOption?.linkOptions);

  if (!isNewLinkOptionExist && isOldLinkOptionsExist && safeChartOption.linkOptions) {
    const { linkOptions } = safeChartOption;
    safeChartOption.linkOption = {
      clickLink: 'targetDetailSlide',
      options: linkOptions.map<ConnectLinkOption>((linkOption) => ({
        ...linkOption,
        openMethod: 'blank',
        category: 'customLink',
      })),
    };
    delete safeChartOption.linkOptions;
  }

  // NOTE: 기존 ARCHITECTURE > ALERT 조합을 ALERT > REALTIME으로 변환
  const isLegacyAlertWidget =
    widgetChildValue.widgetType === 'ARCHITECTURE' && widgetChildValue.chartType === 'ALERT';

  const widgetType = isLegacyAlertWidget
    ? 'ALERT'
    : convertToWidgetType(widgetChildValue.widgetType);

  return {
    i,
    x: widgetChildValue.x ?? 0,
    y: widgetChildValue.y ?? 0,
    w: widgetChildValue.width ?? 1,
    h: widgetChildValue.height ?? 1,
    widgetMappingId: widgetChildValue.widgetMappingId,
    compName: getWidgetCompName(WIDGET_CHART_TYPE_KEY[chartType], DISPLAY_STYLE_KEY[displayStyle]),
    dataResource: convertToDataResource(widgetChildValue.dataResourceType),
    widgetType,
    chartType,
    titleOption: convertTitleOption(widgetChildValue.titleOption),
    widgetColor: convertToWidgetColor(widgetChildValue.widgetColor),
    createType,
    displayStyle,
    headerType: titleOption.headerType ?? 'basic',
    timePeriod: convertToTimePeriod(widgetChildValue.period),
    chartData: (() => {
      const converted = widgetChildValue.chartData?.map((item, idx) =>
        covertToChartData(item, i, idx, chartType, {
          unitOverride: safeChartOption?.unitOverride?.[idx],
        }),
      );
      return widgetType === 'ALERT' ? migrateAlertChartData(converted) : converted;
    })(),
    colorTheme: getSafeSelectedTheme(widgetChildValue.colorTheme),
    minW: widgetSize?.minW,
    minH: widgetSize?.minH,
    chartOption: safeChartOption
      ? {
          ...safeChartOption,
          referenceInfo,
        }
      : {},
    filterOption,
    filterData: filterOption ? extractFilterData(filterOption.filterList) : undefined,
    calendarTimeRange: convertToCalendarTimeRange(widgetChildValue.value),
    customId: widgetChildValue.customId,
    isInViewport: false,
    originHeight: undefined,
    extraData: convertToExtraData(widgetChildValue.extraData),
  };
};

export const convertListToGridLayoutItem: WidgetInPort['convertListToGridLayoutItem'] = (
  widgetGroupValue,
) => {
  const setGroup = (parent: GridLayoutItem) => {
    const childrenGridLayout = widgetGroupValue
      .filter((widget) => widget.parentUUID === parent?.i && !!widget.widgetUUID)
      .map((child) => setGroup(convertToGridLayoutItem(child)));

    return parent ? { ...parent, children: childrenGridLayout } : null;
  };

  const parentWidget = widgetGroupValue.find((widget) => isNil(widget.parentUUID));
  return parentWidget ? setGroup(convertToGridLayoutItem(parentWidget)) : null;
};

export const convertToGridLayoutItemList: WidgetInPort['convertToGridLayoutItemList'] = (
  widgetGroupValue,
) => {
  return widgetGroupValue.map(convertListToGridLayoutItem);
};

const convertToCreateTypeDto: WidgetOutPort['convertToCreateTypeDto'] = (createType) => {
  const createTypeDto = createTypeDtoMap[createType];
  if (!createTypeDto) {
    throw new Error(`${createType} createType을 추가해주세요.`);
  }

  return createTypeDto;
};

const convertToExtraDataDto: WidgetOutPort['convertToExtraDataDto'] = (extraData) => {
  return extraData?.map((data) => ({
    extraId: data.extraId,
    targets: data.targets.map((target) =>
      target.map((item) => ({
        id: item.id,
        value: item.value,
      })),
    ),
  }));
};

export const convertToWidgetGroupValueResponseV8: WidgetOutPort['convertToWidgetGroupValueResponseV8'] =
  (gridLayoutItem, parentUUID) => {
    let chartOption = '{}';
    if (
      [
        'TIME_SERIES',
        'SCATTER',
        'ONE_DAY_COMPARE',
        'SCOREBOARD',
        'STACKED_PROPORTION_BAR',
        'GAUGE',
        // 'HEATMAP',
      ].includes(gridLayoutItem.chartType)
    ) {
      chartOption = JSON.stringify({
        ...gridLayoutItem.chartOption,
        unitOverride: gridLayoutItem.chartData?.map((chartData) => chartData.unitOverride),
      });
    } else {
      chartOption = JSON.stringify(gridLayoutItem.chartOption);
    }

    return {
      widgetUUID: gridLayoutItem.i,
      parentUUID: parentUUID ?? undefined,
      x: gridLayoutItem.x,
      y: gridLayoutItem.y,
      width: gridLayoutItem.w,
      height: gridLayoutItem.h,
      widgetType: gridLayoutItem.widgetType,
      chartType: gridLayoutItem.chartType,
      displayStyle: gridLayoutItem.displayStyle,
      titleOption: JSON.stringify({
        ...gridLayoutItem.titleOption,
        headerType: gridLayoutItem.headerType,
      }),
      widgetColor: JSON.stringify(gridLayoutItem.widgetColor),
      chartOption,
      widgetMappingId: gridLayoutItem.widgetMappingId,
      filterOption:
        gridLayoutItem.filterOption != null
          ? JSON.stringify(gridLayoutItem.filterOption)
          : undefined,
      filterData: gridLayoutItem.filterData,
      period: gridLayoutItem.timePeriod,
      value: gridLayoutItem.calendarTimeRange
        ? JSON.stringify(gridLayoutItem.calendarTimeRange)
        : '',
      colorTheme: gridLayoutItem.colorTheme,
      customId: gridLayoutItem.customId,
      chartData:
        gridLayoutItem.chartData?.map((chartData) => {
          if (chartData.dataId === FIXED_DATA_ID_BY_CHART_TYPE.CLUSTER) {
            syncTargetsToValueTags(chartData as ClusterWidgetChartData);
          }

          return {
            ...omit(chartData, 'externalOptions', 'unitOverride'),
            targets: chartData.targets.map((target) => ({
              ...target,
              tagValueId: target.tagValue,
            })),
            value: chartData.value ? JSON.stringify(omit(chartData.value, 'categories')) : '',
            extraResourceOption: chartData.externalOptions
              ? JSON.stringify(chartData.externalOptions)
              : undefined,
          };
        }) ?? [],
      createType: convertToCreateTypeDto(gridLayoutItem.createType),
      extraData: convertToExtraDataDto(gridLayoutItem.extraData),
      dataResourceType: gridLayoutItem.dataResource,
    } satisfies DashboardWidgetGroupValueV8;
  };

export const convertToWidgetGroupValueResponseV8Group: WidgetOutPort['convertToWidgetGroupValueResponseV8Group'] =
  (gridLayoutItem, parentUUID) => {
    const { children } = gridLayoutItem;
    if (children && children.length > 0) {
      const exceptChild = omit(gridLayoutItem, 'children');
      return [
        convertToWidgetGroupValueResponseV8(exceptChild, parentUUID),
        ...children.flatMap((child) =>
          convertToWidgetGroupValueResponseV8Group(child, gridLayoutItem.i),
        ),
      ];
    }
    return [convertToWidgetGroupValueResponseV8(gridLayoutItem, parentUUID)];
  };

export const convertToWidgetGroupValueResponseV8List: WidgetOutPort['convertToWidgetGroupValueResponseV8List'] =
  (gridLayoutItemList) => {
    return gridLayoutItemList.map((item) => convertToWidgetGroupValueResponseV8Group(item, null));
  };

export const replaceGlobalFilterIdsInGridLayoutItem = (
  item: GridLayoutItem,
  mapping: Map<number, number>,
): void => {
  // 1. chartData.targets에서 globalVariable/treeGlobalVariable 타입의 tagValue replace
  if (item.chartData) {
    item.chartData.forEach((chartData) => {
      if (chartData.targets) {
        chartData.targets.forEach((target) => {
          if (
            (target.tagType === 'globalVariable' || target.tagType === 'treeGlobalVariable') &&
            !isNil(mapping.get(+target.tagValue))
          ) {
            target.tagValue = String(mapping.get(+target.tagValue));
          }
        });
      }
    });
  }

  // 2. chartOption.referenceInfo에서 globalVariable 타입의 variableId/treeVariableId replace
  if (item.chartOption?.referenceInfo) {
    Object.keys(item.chartOption.referenceInfo).forEach((key) => {
      item.chartOption.referenceInfo[key] =
        item.chartOption.referenceInfo[key]?.map((ref) => {
          if (ref.value?.type === 'globalVariable') {
            const replaceIds = (ids?: string[]) =>
              ids?.map((id: string) => (!isNil(mapping.get(+id)) ? String(mapping.get(+id)) : id));

            return {
              ...ref,
              value: {
                ...ref.value,
                ...(ref.value.variableId && { variableId: replaceIds(ref.value.variableId) }),
                ...(ref.value.treeVariableId && {
                  treeVariableId: replaceIds(ref.value.treeVariableId),
                }),
              },
            };
          }
          return ref;
        }) ?? [];
    });
  }

  // 3. extraData에서 marker 관련 targets의 globalVariable/treeGlobalVariable 타입 replace
  if (item.extraData) {
    item.extraData.forEach((extraData) => {
      if (extraData.extraId === 'marker' && extraData.targets) {
        extraData.targets.forEach((targetGroup) => {
          const tagTypeItem = targetGroup.find((i) => i.id === 'tagType');
          const tagValueItem = targetGroup.find((i) => i.id === 'tagValue');

          if (
            (tagTypeItem?.value === 'globalVariable' ||
              tagTypeItem?.value === 'treeGlobalVariable') &&
            tagValueItem &&
            !isNil(mapping.get(+tagValueItem.value))
          ) {
            tagValueItem.value = String(mapping.get(+tagValueItem.value));
          }
        });
      }
    });
  }
};

/**
 * chartData.targets를 source of truth로 사용하여 위젯 내부 중복 저장된 GlobalVariable ID를 동기화한다.
 * import 시 서버가 chartData.targets만 새 ID로 변경하고 아래 위치는 갱신하지 않기 때문에 프론트에서 보정한다.
 * - Topology: chartOption.nodeOptions[].targets[].tagId
 * - Cluster: chartData[].value.tags[nodeType][].tagId
 * mapping과 무관하게 항상 실행되어야 한다.
 */
export const syncGlobalFilterIdsFromChartData = (item: GridLayoutItem): void => {
  // 1. Topology: chartOption.nodeOptions의 targets를 chartData.targets 기준으로 동기화
  // nodeOption.targets[i]와 chartData.targets[i]는 동일 순서로 대응된다.
  if (item.chartType === 'TOPOLOGY' && item.chartOption?.nodeOptions && item.chartData) {
    item.chartOption.nodeOptions.forEach((nodeOption) => {
      if (!nodeOption.targets) return;

      const matchedChartData = item.chartData?.find((cd) => cd.value?.settingId === nodeOption.id);
      if (!matchedChartData?.targets) return;

      nodeOption.targets.forEach((target, index) => {
        if (
          target.groupTagId === 'Global Variable' ||
          target.groupTagId === 'Tree Global Variable'
        ) {
          const cdTarget = matchedChartData.targets[index];
          if (cdTarget && cdTarget.tagKey === target.groupTagId) {
            target.tagId = cdTarget.tagValue;
          }
        }
      });
    });
  }

  // 2. Cluster: chartData[].value.tags의 tagId를 chartData.targets 기준으로 동기화
  // 서버가 chartData.targets에 targetType을 포함하지 않으므로 tagKey로 매칭한다.
  // 동일 Global Variable이 모든 targetType에 공유되므로 tagKey 매칭으로 충분하다.
  if (item.chartType === 'CLUSTER' && item.chartData) {
    item.chartData.forEach((chartData) => {
      const tags = chartData.value?.tags as ClusterValueTags | undefined;
      if (!tags || !chartData.targets) return;

      Object.values(tags).forEach((tagList) => {
        tagList.forEach((tag) => {
          if (tag.groupTagId === 'Global Variable' || tag.groupTagId === 'Tree Global Variable') {
            const matchedTarget = chartData.targets.find(
              (cdTarget) => cdTarget.tagKey === tag.groupTagId,
            );
            if (matchedTarget) {
              tag.tagId = matchedTarget.tagValue;
            }
          }
        });
      });
    });
  }
};

export const replaceGlobalFilterIdsInGridLayoutItems = (
  gridLayoutItems: GridLayoutItem[],
  mapping: Map<number, number>,
): void => {
  forEachGridLayoutItems(gridLayoutItems, (item) => {
    replaceGlobalFilterIdsInGridLayoutItem(item, mapping);
  });
};

export const processGlobalFilterMapping = (
  data: WidgetV8MappingResponseItem['widgets'],
  mapping: Map<number, number>,
) => {
  const gridLayoutItems = convertToGridLayoutItemList(data ?? []);

  if (mapping.size > 0) {
    replaceGlobalFilterIdsInGridLayoutItems(gridLayoutItems, mapping);
  }

  forEachGridLayoutItems(gridLayoutItems, syncGlobalFilterIdsFromChartData);

  return gridLayoutItems;
};
